import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { isDevAuthBypass, ensureMsalInitialized } from './index';
import { warmSession } from '@lib/http';

/**
 * Route guard for authenticated paths.
 * Per Shipping-Module-Architecture-v1.md §4 — every user signs in via Entra
 * (first-party member) before reaching any Shipping surface.
 *
 * Mirrors the Reports portal kit's AuthRoute, minus the HMAC signed-link cookie
 * branch (there is no B2B / signed-link path in the internal Shipping module —
 * architecture §4.3). The only gate is an MSAL session:
 *  - Dev-mode bypass (no VITE_ENTRA_CLIENT_ID): always allow, so the UI loads
 *    without an Azure app registration.
 *  - Prod: MSAL `useIsAuthenticated`; redirect to /login if not signed in.
 *
 * Role gating (Warehouse vs Admin) is a SEPARATE concern, enforced by
 * <RequireRole> reading RoleContext — authentication here, authorization there.
 */
export function AuthRoute() {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  useEffect(() => {
    ensureMsalInitialized().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('MSAL init failed', e);
    });
  }, [instance]);

  // Warm the cdx_session cookie as soon as the MSAL session is confirmed, so
  // the first /api/board fetch doesn't pay the 401 -> mint -> retry round trip
  // (see src/lib/http.ts warmSession — fire-and-forget, single-flighted, and a
  // no-op under the dev bypass where the mock client serves all data).
  useEffect(() => {
    if (isAuthenticated) warmSession();
  }, [isAuthenticated]);

  if (isDevAuthBypass()) {
    return <Outlet />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
